<template>
  <v-dialog
    :value="visiblePopup"
    :width="700"
    @click:outside="onCloseClickButton"
    @keydown.esc="onCloseClickButton"
  >
    <v-card class="domain-scan-settings-popup popup-wrapper">
      <div class='title-row'>
        <div class='title'>{{ $t('domain.scan.settings.popup.title') }} ({{tld}})</div>
        <span class='close_btn' @click='onCloseClickButton'>
          <span class="material-icons-outlined">
            close
          </span>
        </span>
      </div>
      <v-alert
        v-if="validationError"
        text
        dense
        color="#DF3958"
        icon="warning"
        border="left"
        class='validation-error'
      >
        {{ validationError }}
      </v-alert>
      <div class='content'>
        <v-expansion-panels multiple v-model="panels" flat>
          <!-- Subdomain Filtering Section -->
          <v-expansion-panel>
            <v-expansion-panel-header class='panel-header'>
              {{ $t('domain.scan.settings.popup.subdomain.filters.title') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="filters-content">
                <div class="filters-list">
                  <div v-for="(filter, index) in subdomainFilters" :key="index" class="filter-row">
                    <div class="text-field-section">
                      <v-text-field
                        v-model="subdomainFilters[index].pattern"
                        dense
                        outlined
                        @change="updateSettings"
                        class="filter-input"
                        :label="$t('domain.scan.settings.popup.filters.pattern') + ' #' + (index+1)"
                      />
                    </div>
                    <div class="select-section">
                      <v-select
                        v-model="subdomainFilters[index].behavior"
                        :items="filterBehaviors"
                        dense
                        outlined
                        @change="updateSettings"
                        :append-icon="'expand_more'"
                        placeholder="$t('select')"
                        :label="$t('domain.scan.settings.popup.filters.behavior')"
                      />
                    </div>
                    <div class="delete-row-section">
                      <custom-tooltip popper-class="light-tooltip" placement="right">
                        <div slot='buttonContent'>
                          <span
                            class="delete-icon material-icons"
                            :title="$t('delete')"
                            @click="removeSubdomainFilter(index)"
                          >
                            highlight_off
                          </span>
                        </div>
                        <div slot="content">
                          {{$t('delete')}}
                        </div>
                      </custom-tooltip>
                    </div>
                  </div>
                </div>
                <div class="add-new-filter-section">
                  <div class="add-new-filter-header">
                    <label class="add-new-filter-button" @click="addSubdomainFilter">
                      <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                    </label>
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>

          <!-- Header Settings Section -->
          <v-expansion-panel>
            <v-expansion-panel-header class='panel-header'>
              {{ $t('domain.scan.settings.popup.headers.title') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="filters-content">
                <div class="filters-list">
                  <div v-for="(header, index) in customHeaders" :key="index" class="filter-row">
                    <div class="text-field-section short">
                      <v-text-field
                        v-model="customHeaders[index].name"
                        dense
                        outlined
                        @change="updateSettings"
                        class="name-filter-input"
                        :label="$t('domain.scan.settings.popup.headers.name') + ' #' + (index+1)"
                      />
                    </div>
                    <div class="text-field-section">
                      <v-text-field
                        v-model="customHeaders[index].value"
                        dense
                        outlined
                        @change="updateSettings"
                        class="value-filter-input"
                        :label="$t('domain.scan.settings.popup.headers.value') + ' #' + (index+1)"
                      />
                    </div>
                    <div class="delete-row-section">
                      <custom-tooltip popper-class="light-tooltip" placement="right">
                        <div slot='buttonContent'>
                          <span
                            class="delete-icon material-icons"
                            :title="$t('delete')"
                            @click="removeHeader(index)"
                          >
                            highlight_off
                          </span>
                        </div>
                        <div slot="content">
                          {{$t('delete')}}
                        </div>
                      </custom-tooltip>
                    </div>
                  </div>
                </div>
                <div class="add-new-filter-section">
                  <div class="add-new-filter-header">
                    <label class="add-new-filter-button" @click="addHeader">
                      <span class="plus">+</span>{{ $t('add.new.email.popup.button') }}
                    </label>
                  </div>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
      <div class='actions'>
        <div class='btn-section'>
          <v-btn
            small
            depressed
            class="cancel-button"
            @click="onCloseClickButton"
          >
            {{ cancelDomainScanSettingsButtonCaption }}
          </v-btn>
          <v-btn small depressed class="update-button" :loading='loading' :disabled="disableSaveButton" @click="updateSensitivity()">
            {{ updateDomainScanSettingsButtonCaption }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

export default {
  name: 'DomainScanSettingsPopup',
  components: {
    CustomTooltip,
  },
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialSubdomainFilters: {
      type: Array,
      default: () => [],
    },
    initialCustomHeaders: {
      type: Array,
      default: () => [],
    },
    tld: {
      type: String,
      required: true,
    },
    website: {
      required: true,
    },
  },
  data() {
    return {
      panels: [0, 1],
      subdomainFilters: [],
      customHeaders: [],
      filterBehaviors: [
        { text: this.$t('domain.scan.settings.popup.filters.behavior.endsWith'), value: 'endsWith' },
        { text: this.$t('domain.scan.settings.popup.filters.behavior.equals'), value: 'equals' },
        { text: this.$t('domain.scan.settings.popup.filters.behavior.includes'), value: 'includes' },
      ],
      initialSettings: null,
      validationPatterns: {
        subdomainPattern: /^[a-zA-Z0-9.-]+$/,
        headerName: /^[a-zA-Z0-9-_]+$/,
        headerValue: /^[a-zA-Z0-9_\-:;.,/"'?!(){}[\]@<>=\-+*#$&`|~^%]+$/,
      },
    };
  },
  computed: {
    updateDomainScanSettingsButtonCaption() {
      return this.$t('update.domain.settings.popup.button');
    },
    cancelDomainScanSettingsButtonCaption() {
      return this.$t('cancel.domain.settings.popup.button');
    },
    validationError() {
      // Check for empty fields
      const hasEmptySubdomainFilters = this.subdomainFilters.some(filter => !filter.pattern.trim());
      const hasEmptyHeaders = this.customHeaders.some(header => !header.name.trim() || !header.value.trim());

      if (hasEmptySubdomainFilters) {
        return this.$t('domain.scan.settings.popup.validation.empty.subdomain');
      }
      if (hasEmptyHeaders) {
        return this.$t('domain.scan.settings.popup.validation.empty.headers');
      }

      // Check for invalid subdomain patterns
      const invalidSubdomainPattern = this.subdomainFilters.find(filter => !this.validationPatterns.subdomainPattern.test(filter.pattern.trim()));
      if (invalidSubdomainPattern) {
        return this.$t('domain.scan.settings.popup.validation.invalid.subdomain.pattern');
      }

      // Check for invalid header names
      const invalidHeaderName = this.customHeaders.find(header => !this.validationPatterns.headerName.test(header.name.trim()));
      if (invalidHeaderName) {
        return this.$t('domain.scan.settings.popup.validation.invalid.header.name');
      }

      // Check for invalid header values
      const invalidHeaderValue = this.customHeaders.find(header => !this.validationPatterns.headerValue.test(header.value.trim()));
      if (invalidHeaderValue) {
        return this.$t('domain.scan.settings.popup.validation.invalid.header.value');
      }

      // Check for duplicate patterns in subdomain filters
      const patterns = this.subdomainFilters.map(filter => filter.pattern.trim().toLowerCase());
      const uniquePatterns = new Set(patterns);
      if (patterns.length !== uniquePatterns.size) {
        return this.$t('domain.scan.settings.popup.validation.duplicate.patterns');
      }

      // Check for duplicate header names
      const headerNames = this.customHeaders.map(header => header.name.trim().toLowerCase());
      const uniqueHeaderNames = new Set(headerNames);
      if (headerNames.length !== uniqueHeaderNames.size) {
        return this.$t('domain.scan.settings.popup.validation.duplicate.headers');
      }

      return '';
    },
    disableSaveButton() {
      if (this.validationError) {
        return true;
      }

      if (!this.initialSettings) {
        return true;
      }

      const currentSettings = {
        subdomainFilters: this.subdomainFilters,
        customHeaders: this.customHeaders,
      };

      // Deep comparison of objects
      const hasChanges = JSON.stringify(this.initialSettings) !== JSON.stringify(currentSettings);
      return !hasChanges;
    },
  },
  watch: {
    visiblePopup: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Initialize with empty lists if no initial data
          if (!this.initialSubdomainFilters || this.initialSubdomainFilters.length === 0) {
            this.subdomainFilters = [];
          } else {
            this.subdomainFilters = JSON.parse(JSON.stringify(this.initialSubdomainFilters));
          }
          if (!this.initialCustomHeaders || this.initialCustomHeaders.length === 0) {
            this.customHeaders = [];
          } else {
            this.customHeaders = JSON.parse(JSON.stringify(this.initialCustomHeaders));
          }

          // Store initial settings
          this.initialSettings = {
            subdomainFilters: JSON.parse(JSON.stringify(this.subdomainFilters)),
            customHeaders: JSON.parse(JSON.stringify(this.customHeaders)),
          };
        }
      },
    },
  },
  methods: {
    onCloseClickButton() {
      this.$emit('close');
    },
    async updateSensitivity() {
      if (this.disableSaveButton) {
        return;
      }
      await this.$emit('updateDomainSettings', {
        tld: this.tld,
        website: this.website,
        scanSettings: { subdomainFilters: this.subdomainFilters, customHeaders: this.customHeaders},
      });
    },
    addSubdomainFilter() {
      this.subdomainFilters.push({
        pattern: '',
        behavior: 'endsWith',
      });
    },
    removeSubdomainFilter(index) {
      this.subdomainFilters.splice(index, 1);
      if (this.subdomainFilters.length === 0) {
        this.subdomainFilters = [];
      }
    },
    addHeader() {
      this.customHeaders.push({
        name: '',
        value: '',
      });
    },
    removeHeader(index) {
      this.customHeaders.splice(index, 1);
      if (this.customHeaders.length === 0) {
        this.customHeaders = [];
      }
    },
    updateSettings() {
      // This method can be used to validate or process the settings before saving
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.domain-scan-settings-popup {
  .v-input__slot {
    background-color: var(--bg-primary) !important;
  }
  .v-expansion-panel {
    background-color: transparent !important;
  }
  .v-expansion-panel-header {
    background-color: transparent !important;
  }
  .v-expansion-panel-content__wrap {
    background-color: transparent !important;
  }
  .v-slider__tick-label {
    padding: $space-1 $space-2;
    width: rem(75);
    text-align: center;
    text-transform: capitalize;
  }
  .v-slider__tick:first-of-type {
    .v-slider__tick-label {
      color: var(--severity-low-text);
      background-color: var(--severity-low-bg);
    }
  }
  .v-slider__tick{
    .v-slider__tick-label {
      color: var(--severity-medium-text);
      background-color: var(--severity-medium-bg);
    }
  }
  .v-slider__tick:last-of-type {
    .v-slider__tick-label {
      color: var(--severity-critical-text);
      background-color: var(--severity-critical-bg);
    }
  }
  .v-slider {
    .v-slider__tick-label {
      margin-top: rem(12);
    }
  }
}
</style>
<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.domain-scan-settings-popup {
  .validation-error{
    font-family: $font-family-regular;
  }
  .v-slider{
    .v-slider__tick-label{
      margin-top: rem(12);
    }
  }
  .content{
    .panel-header {
      color: var(--text-primary);
      font-family: $font-family-medium;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: rem(17);
      text-transform: uppercase;
    }
    .filters-content {
      padding: rem(10);
      .name-filter-input {
        max-width: rem(200);
      }
      .value-filter-input {
        flex-shrink: 1;
        flex-grow: 1;
        flex-basis: auto;
      }
      .filters-list {
        margin-bottom: rem(10);

        .filter-row {
          display: flex;
          align-items: center;
          margin-top: $space-6;

          .text-field-section {
            flex-grow: 1;
            flex-shrink: 1;
            flex-basis: 90%;
            margin-right: rem(10);
            &.short {
                flex-basis: 40%;
            }
          }
          .select-section {
            max-width: rem(150);
            ::v-deep input{
              margin-top: rem(-18);
            }
          }

          .delete-row-section {
            flex: 1;
            display: flex;
            align-items: center;

            .delete-icon {
              color: var(--severity-critical);
              cursor: pointer;
              font-size: $space-4;
              margin-left: rem(10);
              font-weight: bolder;
            }
          }
        }
      }

      .add-new-filter-section {
        padding: $space-0;

        .add-new-filter-header {
          display: flex;
          justify-content: space-between;

          .add-new-filter-button {
            color: var(--brand-primary);
            text-transform: uppercase;
            font-size: $font-size-sm;
            font-weight: 500;
            letter-spacing: 1.07px;
            line-height: $space-4;
            text-align: center;
            margin-right: rem(12);
            cursor: pointer;

            .plus {
              font-size: $space-4;
            }
          }
        }
      }
    }
  }
  &.popup-wrapper {
    width: rem(700);
    padding: rem(24) $space-8;
    display: flex;
    flex-direction: column;
    background-color: var(--bg-primary) !important;
    .actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-top: rem(35);
      .btn-section {
        display: flex;
        justify-content: flex-end;
        .update-button {
          height: $space-8;
          width: rem(51);
          border-radius: $radius-sm;
          background-color: var(--brand-primary) !important;
          box-shadow: $shadow-sm;
          color: var(--text-inverse) !important;
          font-size: $font-size-sm;
          font-weight: 500;
          letter-spacing: 1.07px;
          line-height: $space-4;
          text-align: center;
          margin-left: $space-2;
        }
        .cancel-button {
          box-sizing: border-box;
          height: $space-8;
          width: rem(86);
          border: $border-width-thin solid var(--brand-primary) !important;
          color: var(--brand-primary) !important;
          border-radius: $radius-sm;
          background-color: var(--bg-primary) !important;
          box-shadow: $shadow-sm;
        }
      }
    }
    .title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: rem(35);
      .title {
        color: var(--text-primary);
        font-family: $font-family-bold;
        font-size: rem(24);
        font-weight: bold;
        line-height: $font-size-xl;
        letter-spacing: 0;
      }
      .close_btn {
        cursor: pointer;
        &:hover {
          color: var(--brand-primary);
        }
      }
    }
    .actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding-top: rem(30);
      .btn-section {
        display: flex;
        justify-content: flex-end;
      }
    }
  }
}
</style>
